import {
  CARTO_STYLE,
  googleAuthFailure,
  loadMapLibre,
  waitForGoogleMaps,
} from '../../../../maps/basemap';
import { Bounds, ScreenPoint } from './map-types';

/**
 * The suburb map's basemap (#256; plan §6.1): Google Maps (already loaded by index.html) where Conductor's key is
 * allowed; elsewhere (a local port the key's referrer list doesn't cover) MapLibre with CARTO's keyless tiles, also
 * vendored. Only the basemap differs: both carry the same deck.gl overlay, behind the small MapAdapter below.
 */

declare const google: any;

/** How long to wait for Google's script, and then for its first tiles, before trusting the key. */
const GOOGLE_LOAD_MS = 8000;
const GOOGLE_TILES_MS = 5000;
/** Where the map opens before a state is loaded: Sydney. */
const START = { lat: -33.87, lng: 151.21 };
const GOOGLE_START_ZOOM = 10;
const MAPLIBRE_START_ZOOM = 9;
/** Pixels kept clear around a fitted area (the side panel's width is added on the right). */
const GOOGLE_FIT_PADDING = 20;
const MAPLIBRE_FIT_PADDING = 30;

/** What the engine needs from a basemap. */
export interface MapAdapter {
  kind: 'google' | 'maplibre';
  /** Fit the bounds, keeping `right` pixels clear on the right (where the workspace's side panel sits). */
  fitBounds(b: Bounds, right: number): void;
  setDraggable(on: boolean): void;
  project(lng: number, lat: number): ScreenPoint | null;
  setView(lng: number, lat: number, zoom: number): void;
  center(): [number, number];
  zoom(): number;
  resize(): void;
  destroy(): void;
}

export interface Basemap {
  map: MapAdapter;
  /** deck.gl's overlay on it: the engine sets its layers and picks through it. */
  overlay: any;
}

export interface BasemapEvents {
  zoom(z: number): void;
  /** The view moved (pan or zoom). */
  view(): void;
}

/** Google where it serves Conductor's key here, else the open basemap. `el` is emptied of Google's error panel first. */
export async function openBasemap(el: HTMLElement, deck: any, on: BasemapEvents): Promise<Basemap> {
  let onGoogle: Basemap | null = null;
  try {
    await waitForGoogleMaps(GOOGLE_LOAD_MS);
    onGoogle = await openGoogle(el, deck, on);
  } catch {
    onGoogle = null; // the script never loaded, or Google threw building the map: the open basemap serves
  }
  if (onGoogle) return onGoogle;
  el.innerHTML = ''; // Google's "something went wrong" panel; the lasso canvas is a sibling, not a child
  // The workspace shows GOOGLE_REFUSED_NOTE for as long as the open basemap is in use.
  return openMapLibre(el, deck, on);
}

/** Google basemap; null when Google refuses the key here (checked against the first tiles, ≤ 5 s). */
async function openGoogle(el: HTMLElement, deck: any, on: BasemapEvents): Promise<Basemap | null> {
  const failed = googleAuthFailure().then(() => 'failed' as const);
  if ((window as any).__gmAuthFailed) return null;
  const gmap = new google.maps.Map(el, {
    center: START,
    zoom: GOOGLE_START_ZOOM,
    mapTypeId: 'roadmap',
    disableDefaultUI: true,
    zoomControl: true,
    scaleControl: true,
    gestureHandling: 'greedy',
    clickableIcons: false,
    styles: [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ],
  });
  const tiles = new Promise<'ok'>((resolve) =>
    google.maps.event.addListenerOnce(gmap, 'tilesloaded', () => resolve('ok'))
  );
  const timeout = new Promise<'ok'>((resolve) => setTimeout(() => resolve('ok'), GOOGLE_TILES_MS));
  if ((await Promise.race([failed, tiles, timeout])) === 'failed') {
    google.maps.event.clearInstanceListeners(gmap);
    return null;
  }
  const overlay = new deck.GoogleMapsOverlay({ layers: [] });
  overlay.setMap(gmap);
  const zoomListener = gmap.addListener('zoom_changed', () => on.zoom(gmap.getZoom()));
  const viewListener = gmap.addListener('bounds_changed', () => on.view());
  const map: MapAdapter = {
    kind: 'google',
    fitBounds: (b, right) => {
      const pad = GOOGLE_FIT_PADDING;
      gmap.fitBounds(
        new google.maps.LatLngBounds({ lng: b[0], lat: b[1] }, { lng: b[2], lat: b[3] }),
        { top: pad, bottom: pad, left: pad, right: right + pad }
      );
    },
    setDraggable: (on) => gmap.setOptions({ draggable: on }),
    project: (lng, lat) => projectGoogle(gmap, lng, lat),
    setView: (lng, lat, zoom) => {
      gmap.setCenter({ lat, lng });
      gmap.setZoom(zoom);
    },
    center: () => [gmap.getCenter().lng(), gmap.getCenter().lat()],
    zoom: () => gmap.getZoom(),
    resize: () => google.maps.event.trigger(gmap, 'resize'),
    destroy: () => {
      zoomListener.remove();
      viewListener.remove();
      overlay.setMap(null);
      google.maps.event.clearInstanceListeners(gmap);
    },
  };
  return { map, overlay };
}

/** Lat/lng to pixels in the map element: Google has no project() of its own, so through its world coordinates. */
function projectGoogle(gmap: any, lng: number, lat: number): ScreenPoint | null {
  const proj = gmap.getProjection();
  const bounds = gmap.getBounds();
  if (!proj || !bounds) return null;
  const scale = 2 ** gmap.getZoom();
  const nw = proj.fromLatLngToPoint(
    new google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getSouthWest().lng())
  );
  const pt = proj.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
  return nw && pt ? { x: (pt.x - nw.x) * scale, y: (pt.y - nw.y) * scale } : null;
}

/** Keyless fallback: MapLibre + CARTO Positron. */
async function openMapLibre(el: HTMLElement, deck: any, on: BasemapEvents): Promise<Basemap> {
  const maplibregl = await loadMapLibre();
  const m = new maplibregl.Map({
    container: el,
    style: CARTO_STYLE,
    center: [START.lng, START.lat],
    zoom: MAPLIBRE_START_ZOOM,
    attributionControl: { compact: true },
    dragRotate: false,
    pitchWithRotate: false,
  });
  m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
  m.addControl(new maplibregl.ScaleControl(), 'bottom-right');
  await new Promise((resolve) => m.on('load', resolve));
  const overlay = new deck.MapboxOverlay({ interleaved: false, layers: [] });
  m.addControl(overlay);
  m.on('zoom', () => on.zoom(m.getZoom()));
  m.on('move', () => on.view());
  const pad = MAPLIBRE_FIT_PADDING;
  const map: MapAdapter = {
    kind: 'maplibre',
    fitBounds: (b, right) =>
      m.fitBounds(
        [
          [b[0], b[1]],
          [b[2], b[3]],
        ],
        { padding: { top: pad, bottom: pad, left: pad, right: right + pad }, duration: 0 }
      ),
    setDraggable: (on) => (on ? m.dragPan.enable() : m.dragPan.disable()),
    project: (lng, lat) => {
      const p = m.project([lng, lat]);
      return { x: p.x, y: p.y };
    },
    setView: (lng, lat, zoom) => m.jumpTo({ center: [lng, lat], zoom }),
    center: () => [m.getCenter().lng, m.getCenter().lat],
    zoom: () => m.getZoom(),
    resize: () => m.resize(),
    destroy: () => m.remove(),
  };
  on.zoom(m.getZoom());
  return { map, overlay };
}
